import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save, X, Plus, Trash2 } from 'lucide-react';

interface PurchaseOrderItem {
  itemId: string;
  itemName: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  description?: string;
}

interface PurchaseOrderFormData {
  supplierId: string;
  orderNumber: string;
  orderDate: string;
  expectedDeliveryDate: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'draft' | 'pending_approval' | 'approved' | 'sent' | 'received' | 'cancelled';
  paymentTerms: string;
  deliveryAddress: string;
  notes: string;
  items: PurchaseOrderItem[];
}

interface PurchaseOrderFormProps {
  initialData?: Partial<PurchaseOrderFormData>;
  onSubmit: (data: PurchaseOrderFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

const priorities = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' }
];

const statuses = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'sent', label: 'Sent to Supplier' },
  { value: 'received', label: 'Received' },
  { value: 'cancelled', label: 'Cancelled' }
];

const paymentTermsOptions = [
  { value: 'net_30', label: 'Net 30 Days' },
  { value: 'net_60', label: 'Net 60 Days' },
  { value: 'net_90', label: 'Net 90 Days' },
  { value: 'cod', label: 'Cash on Delivery' },
  { value: 'prepaid', label: 'Prepaid' },
  { value: '2_10_net_30', label: '2/10 Net 30' }
];

const PurchaseOrderForm: React.FC<PurchaseOrderFormProps> = ({
  initialData = {},
  onSubmit,
  onCancel,
  isLoading = false,
  mode
}) => {
  const { toast } = useToast();
  
  const [formData, setFormData] = useState<PurchaseOrderFormData>({
    supplierId: initialData.supplierId || '',
    orderNumber: initialData.orderNumber || '',
    orderDate: initialData.orderDate || new Date().toISOString().split('T')[0],
    expectedDeliveryDate: initialData.expectedDeliveryDate || '',
    priority: initialData.priority || 'medium',
    status: initialData.status || 'draft',
    paymentTerms: initialData.paymentTerms || 'net_30',
    deliveryAddress: initialData.deliveryAddress || '',
    notes: initialData.notes || '',
    items: initialData.items || []
  });

  const [errors, setErrors] = useState<any>({});
  const [availableSuppliers, setAvailableSuppliers] = useState<any[]>([]);
  const [availableItems, setAvailableItems] = useState<any[]>([]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        supplierId: initialData.supplierId || '',
        orderNumber: initialData.orderNumber || '',
        orderDate: initialData.orderDate || new Date().toISOString().split('T')[0],
        expectedDeliveryDate: initialData.expectedDeliveryDate || '',
        priority: initialData.priority || 'medium',
        status: initialData.status || 'draft',
        paymentTerms: initialData.paymentTerms || 'net_30',
        deliveryAddress: initialData.deliveryAddress || '',
        notes: initialData.notes || '',
        items: initialData.items || []
      });
    }
  }, [initialData]);

  const validateForm = (): boolean => {
    const newErrors: any = {};

    if (!formData.supplierId) {
      newErrors.supplierId = 'Supplier is required';
    }

    if (!formData.orderNumber.trim()) {
      newErrors.orderNumber = 'Order number is required';
    }

    if (!formData.orderDate) {
      newErrors.orderDate = 'Order date is required';
    }

    if (!formData.expectedDeliveryDate) {
      newErrors.expectedDeliveryDate = 'Expected delivery date is required';
    }

    if (formData.items.length === 0) {
      newErrors.items = 'At least one item is required';
    }

    formData.items.forEach((item, index) => {
      if (!item.itemId) {
        newErrors[`item_${index}_itemId`] = 'Item is required';
      }
      if (!item.quantity || item.quantity <= 0) {
        newErrors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
      }
      if (!item.unitPrice || item.unitPrice <= 0) {
        newErrors[`item_${index}_unitPrice`] = 'Unit price must be greater than 0';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof PurchaseOrderFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors((prev: any) => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleItemChange = (index: number, field: keyof PurchaseOrderItem, value: any) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };

    // Calculate total price when quantity or unit price changes
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = field === 'quantity' ? value : updatedItems[index].quantity;
      const unitPrice = field === 'unitPrice' ? value : updatedItems[index].unitPrice;
      updatedItems[index].totalPrice = quantity * unitPrice;
    }
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));

    const errorKey = `item_${index}_${field}`;
    if (errors[errorKey]) {
      setErrors((prev: any) => ({
        ...prev,
        [errorKey]: undefined
      }));
    }
  };

  const addItem = () => {
    const newItem: PurchaseOrderItem = {
      itemId: '',
      itemName: '',
      sku: '',
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0,
      description: ''
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const removeItem = (index: number) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const calculateTotalAmount = () => {
    return formData.items.reduce((sum, item) => sum + item.totalPrice, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fix the errors in the form before submitting.",
      });
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Purchase Order Details</CardTitle>
          <CardDescription>
            Enter the basic details for the purchase order
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="supplierId">Supplier *</Label>
              <Select 
                value={formData.supplierId} 
                onValueChange={(value) => handleInputChange('supplierId', value)}
              >
                <SelectTrigger className={errors.supplierId ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent>
                  {availableSuppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.supplierId && <p className="text-sm text-red-500">{errors.supplierId}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="orderNumber">Order Number *</Label>
              <Input
                id="orderNumber"
                value={formData.orderNumber}
                onChange={(e) => handleInputChange('orderNumber', e.target.value)}
                placeholder="Enter order number"
                className={errors.orderNumber ? 'border-red-500' : ''}
              />
              {errors.orderNumber && <p className="text-sm text-red-500">{errors.orderNumber}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="orderDate">Order Date *</Label>
              <Input
                id="orderDate"
                type="date"
                value={formData.orderDate}
                onChange={(e) => handleInputChange('orderDate', e.target.value)}
                className={errors.orderDate ? 'border-red-500' : ''}
              />
              {errors.orderDate && <p className="text-sm text-red-500">{errors.orderDate}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="expectedDeliveryDate">Expected Delivery Date *</Label>
              <Input
                id="expectedDeliveryDate"
                type="date"
                value={formData.expectedDeliveryDate}
                onChange={(e) => handleInputChange('expectedDeliveryDate', e.target.value)}
                className={errors.expectedDeliveryDate ? 'border-red-500' : ''}
              />
              {errors.expectedDeliveryDate && <p className="text-sm text-red-500">{errors.expectedDeliveryDate}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select 
                value={formData.priority} 
                onValueChange={(value: 'low' | 'medium' | 'high' | 'urgent') => handleInputChange('priority', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  {priorities.map((priority) => (
                    <SelectItem key={priority.value} value={priority.value}>
                      {priority.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(value: any) => handleInputChange('status', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="paymentTerms">Payment Terms</Label>
              <Select 
                value={formData.paymentTerms} 
                onValueChange={(value) => handleInputChange('paymentTerms', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select payment terms" />
                </SelectTrigger>
                <SelectContent>
                  {paymentTermsOptions.map((term) => (
                    <SelectItem key={term.value} value={term.value}>
                      {term.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="deliveryAddress">Delivery Address</Label>
            <Textarea
              id="deliveryAddress"
              value={formData.deliveryAddress}
              onChange={(e) => handleInputChange('deliveryAddress', e.target.value)}
              placeholder="Enter delivery address"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Enter any additional notes"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Order Items</CardTitle>
              <CardDescription>
                Add items to the purchase order
              </CardDescription>
            </div>
            <Button type="button" onClick={addItem} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {errors.items && <p className="text-sm text-red-500">{errors.items}</p>}
          
          {formData.items.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <p className="text-muted-foreground">No items added yet</p>
              <Button type="button" onClick={addItem} variant="outline" size="sm" className="mt-2">
                <Plus className="h-4 w-4 mr-2" />
                Add First Item
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {formData.items.map((item, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="font-medium">Item {index + 1}</h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Item *</Label>
                      <Select 
                        value={item.itemId} 
                        onValueChange={(value) => {
                          const selectedItem = availableItems.find(i => i.id === value);
                          if (selectedItem) {
                            handleItemChange(index, 'itemId', value);
                            handleItemChange(index, 'itemName', selectedItem.name);
                            handleItemChange(index, 'sku', selectedItem.sku);
                          }
                        }}
                      >
                        <SelectTrigger className={errors[`item_${index}_itemId`] ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Select item" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableItems.map((availableItem) => (
                            <SelectItem key={availableItem.id} value={availableItem.id}>
                              {availableItem.name} ({availableItem.sku})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors[`item_${index}_itemId`] && (
                        <p className="text-sm text-red-500">{errors[`item_${index}_itemId`]}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Quantity *</Label>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', Number(e.target.value))}
                        min="1"
                        className={errors[`item_${index}_quantity`] ? 'border-red-500' : ''}
                      />
                      {errors[`item_${index}_quantity`] && (
                        <p className="text-sm text-red-500">{errors[`item_${index}_quantity`]}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Unit Price *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.unitPrice}
                        onChange={(e) => handleItemChange(index, 'unitPrice', Number(e.target.value))}
                        min="0"
                        className={errors[`item_${index}_unitPrice`] ? 'border-red-500' : ''}
                      />
                      {errors[`item_${index}_unitPrice`] && (
                        <p className="text-sm text-red-500">{errors[`item_${index}_unitPrice`]}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Total Price</Label>
                      <Input
                        value={item.totalPrice.toFixed(2)}
                        disabled
                        className="bg-gray-50"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label>Description</Label>
                    <Input
                      value={item.description || ''}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      placeholder="Item description (optional)"
                    />
                  </div>
                </div>
              ))}

              <div className="border-t pt-4">
                <div className="flex justify-end">
                  <div className="text-right">
                    <p className="text-lg font-semibold">
                      Total Amount: ${calculateTotalAmount().toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {mode === 'create' ? 'Create Purchase Order' : 'Update Purchase Order'}
        </Button>
      </div>
    </form>
  );
};

export default PurchaseOrderForm; 